/**
 * API Client - HTTP client for backend API calls
 * Handles authentication and request/response formatting
 */

import { config } from '../config';
import { refreshAccessToken } from './authClient';
import { getAuthToken } from '../contexts/AuthContext';

// Use the shared config so dev requests go through the Vite proxy when
// VITE_API_URL is empty. A hard fallback here would bypass the proxy and
// hit a dead port (the historical cause of "TypeError: Failed to fetch").
const API_BASE_URL = config.apiUrl;

/** Largest page the project list endpoints serve; fetch-all loops use it. */
const PROJECTS_PAGE_SIZE = 100;
/** Sanity cap for fetch-all loops (100 pages × 100 rows). */
const PROJECTS_MAX_PAGES = 100;

/**
 * API Key types
 */
export interface ApiKeyListItem {
	id: string;
	name: string;
	keyPrefix: string;
	lastUsedAt: string | null;
	expiresAt: string | null;
	createdAt: string;
	isActive: boolean;
}

export interface ApiKeyWithSecret {
	id: string;
	name: string;
	key: string; // Full API key (only shown once)
	keyPrefix: string;
	expiresAt: string | null;
	createdAt: string;
}

export interface CreateApiKeyRequest {
	name: string;
	expiresInDays?: number;
}

/**
 * Generic API response type
 */
export interface ApiResponse<T> {
	success: boolean;
	data?: T;
	error?: string;
	message?: string;
}

export interface ChatMessage {
	id: string;
	projectId: string;
	userId: string;
	role: 'user' | 'assistant' | 'system';
	content: string;
	chatMode: 'plan' | 'act' | null;
	sessionId: string | null;
	metadata: Record<string, unknown>;
	createdAt: string;
}

export interface ChatMessagesResponse {
	data: ChatMessage[];
	pagination: {
		page: number;
		pageSize: number;
		total: number;
		totalPages: number;
	};
}

/**
 * Build default request headers. Skips the JSON content-type for FormData
 * bodies (the browser must set the multipart boundary itself).
 */
function buildAuthHeaders(options: RequestInit): Record<string, string> {
	const token = getAuthToken();
	const isFormData =
		typeof FormData !== 'undefined' && options.body instanceof FormData;

	const headers: Record<string, string> = {};
	if (!isFormData) {
		headers['Content-Type'] = 'application/json';
	}
	Object.assign(headers, options.headers as Record<string, string>);
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}
	return headers;
}

/**
 * Make an authenticated request and return the raw Response.
 * Handles the 401 → silent token refresh → single retry flow so every
 * call site benefits from it. Prefer apiJson/apiRequest over using this
 * directly unless you need blobs/streams or status-level control.
 */
async function apiFetch(
	endpoint: string,
	options: RequestInit = {}
): Promise<Response> {
	const token = getAuthToken();
	const headers = buildAuthHeaders(options);

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		...options,
		headers,
	});

	if (response.status === 401 && token) {
		// Access token expired — silently refresh via the platform SSO cookie
		// and retry once (agent/crew webs pattern).
		const replacement = await refreshAccessToken();
		if (replacement) {
			return fetch(`${API_BASE_URL}${endpoint}`, {
				...options,
				headers: {
					...headers,
					'Authorization': `Bearer ${replacement}`,
				},
			});
		}
	}

	return response;
}

/**
 * Make an authenticated request and parse the JSON body.
 * Throws on non-2xx responses (preferring the server-provided error message).
 * Returns undefined for empty (e.g. 204) or non-JSON bodies.
 */
async function apiJson<T>(
	endpoint: string,
	options: RequestInit = {}
): Promise<T> {
	const response = await apiFetch(endpoint, options);
	const text = await response.text().catch(() => '');

	let body: unknown;
	if (text) {
		try {
			body = JSON.parse(text);
		} catch {
			body = undefined;
		}
	}

	if (!response.ok) {
		const error = (body as { error?: string } | undefined)?.error;
		throw new Error(error || `API request failed: ${response.statusText}`);
	}

	return body as T;
}

/** Project list envelope (GET /api/projects; org-scoped lists omit pagination). */
export interface ProjectsPageResponse<T> {
	success: boolean;
	error?: string;
	projects?: T[];
	pagination?: {
		page: number;
		pageSize: number;
		total: number;
		totalPages: number;
	};
}

/** Non-2xx from a paged list fetch, carrying the HTTP status. */
export class ApiStatusError extends Error {
	constructor(
		readonly status: number,
		message: string,
	) {
		super(message);
		this.name = 'ApiStatusError';
	}
}

/**
 * Fetch every page of a project list endpoint. Uses apiFetch directly
 * (rather than apiJson) for status-level control: callers that show a
 * login modal on 401 need the status, which apiJson discards. Endpoints
 * without a pagination block resolve after the first page.
 */
export async function fetchAllProjects<T>(baseEndpoint: string): Promise<T[]> {
	const all: T[] = [];
	let page = 1;
	for (;;) {
		if (page > PROJECTS_MAX_PAGES) return all;
		const separator = baseEndpoint.includes('?') ? '&' : '?';
		const response = await apiFetch(`${baseEndpoint}${separator}page=${page}&pageSize=${PROJECTS_PAGE_SIZE}`);
		const data = (await response.json().catch(() => undefined)) as ProjectsPageResponse<T> | undefined;
		if (!response.ok) {
			throw new ApiStatusError(response.status, data?.error || `API request failed: ${response.statusText}`);
		}
		if (!data?.success) {
			throw new Error(data?.error || 'Failed to fetch projects');
		}
		all.push(...(data.projects ?? []));
		if (page >= (data.pagination?.totalPages ?? 1)) return all;
		page += 1;
	}
}

/**
 * Make an authenticated request against the standard ApiResponse envelope.
 * Throws when the response is non-2xx or the envelope reports failure.
 * Returns the full parsed body (envelope fields + payload fields).
 */
async function apiRequest<T>(
	endpoint: string,
	options: RequestInit = {}
): Promise<T> {
	const body = await apiJson<ApiResponse<unknown>>(endpoint, options);
	if (!body.success) {
		throw new Error(body.error || 'API request failed');
	}
	return body as unknown as T;
}

/**
 * API Keys endpoints
 */
export const apiKeys = {
	/**
	 * List all API keys for the current user
	 */
	async list(): Promise<ApiKeyListItem[]> {
		const response = await apiRequest<{ apiKeys: ApiKeyListItem[] }>(
			'/api/auth/api-keys',
			{ method: 'GET' }
		);
		return response.apiKeys;
	},

	/**
	 * Create a new API key
	 */
	async create(data: CreateApiKeyRequest): Promise<ApiKeyWithSecret> {
		const response = await apiRequest<{ apiKey: ApiKeyWithSecret; message: string }>(
			'/api/auth/api-keys',
			{
				method: 'POST',
				body: JSON.stringify(data),
			}
		);
		return response.apiKey;
	},

	/**
	 * Revoke an API key
	 */
	async revoke(id: string): Promise<void> {
		await apiRequest<{ message: string }>(
			`/api/auth/api-keys/${id}`,
			{ method: 'DELETE' }
		);
	},
};

/**
 * Chat endpoints
 */
export const chat = {
	/**
	 * Get chat messages for a project
	 */
	async getMessages(projectId: string, page = 1, pageSize = 50): Promise<ChatMessagesResponse> {
		const response = await apiRequest<ChatMessagesResponse>(
			`/api/chat/projects/${projectId}/chat?page=${page}&pageSize=${pageSize}`,
			{ method: 'GET' }
		);
		return response;
	},

	/**
	 * Save a chat message
	 */
	async saveMessage(
		projectId: string,
		message: Omit<ChatMessage, 'id' | 'projectId' | 'userId' | 'createdAt'>
	): Promise<{ success: boolean; data: ChatMessage }> {
		const response = await apiRequest<{ success: boolean; data: ChatMessage }>(
			`/api/chat/projects/${projectId}/chat`,
			{
				method: 'POST',
				body: JSON.stringify(message),
			}
		);
		return response;
	},

	/**
	 * Save multiple chat messages (bulk)
	 */
	async saveMessages(
		projectId: string,
		messages: Omit<ChatMessage, 'id' | 'projectId' | 'userId' | 'createdAt'>[]
	): Promise<{ success: boolean; data: { count: number } }> {
		const response = await apiRequest<{ success: boolean; data: { count: number } }>(
			`/api/chat/projects/${projectId}/chat/bulk`,
			{
				method: 'POST',
				body: JSON.stringify({ messages }),
			}
		);
		return response;
	},

	/**
	 * Clear all chat messages for a project
	 */
	async clearMessages(projectId: string): Promise<{ success: boolean; data: { message: string } }> {
		const response = await apiRequest<{ success: boolean; data: { message: string } }>(
			`/api/chat/projects/${projectId}/chat`,
			{ method: 'DELETE' }
		);
		return response;
	},
};

/**
 * Export API client
 */
export const api = {
	apiKeys,
	chat,
};

export { apiFetch, apiJson, apiRequest };
export default api;
